/**
 * Query keys rooted by the entity whose data they cache, not by the screen that reads it.
 *
 * TanStack invalidation matches by prefix, so a write that invalidates `['categories']` reaches a
 * read only if that read's key starts with `'categories'`. A Category dropdown cached under
 * `['devices', 'meta', 'categories']` was never refreshed by a category write and kept serving the
 * old list until a reload. Any feature that reads another entity's data keys it here, under that
 * entity's root, so the entity's own writes reach it with no change to the mutation.
 *
 * `meta(source, params)` keys an option list: `source` names the endpoint variant (a different
 * response shape or filter must never share a cache entry), `params` whatever else varies.
 *
 * A write that changes data another entity displays (a Dealer write moves the Customers listing)
 * invalidates that entity's `all` root too — the declared dependents in ADR-0039.
 */
function entity<const Root extends string>(root: Root) {
  return {
    all: [root] as const,
    meta: (source: string, params: object = {}) => [root, 'meta', source, params] as const,
  };
}

export const queryKeys = {
  categories: entity('categories'),
  devices: entity('devices'),
  components: entity('components'),
  bundles: entity('bundles'),
  customers: entity('customers'),
  adminUsers: entity('admin-users'),
  dealers: entity('dealers'),
  orders: entity('orders'),
  payments: entity('payments'),
  replacements: entity('replacements'),
  warrantyClaims: entity('warranty-claims'),
  dashboard: {
    ...entity('dashboard'),
    /** The open-request counts only. A request write invalidates this branch, never the root:
     *  the tiles and graphs must be unaffected by a request decision (Dashboard grilling Q9). */
    quickActions: ['dashboard', 'quick-actions'] as const,
  },
  deviceTelemetry: entity('device-telemetry'),
  customerMap: entity('customer-map'),
};
